import { MatDialog } from '@angular/material/dialog';
import { FormControlValueChangedAction } from './../models/form-value-changed-event';
import { JsonDynamicComponentService } from './../json-dynamic-component.service';
import { MatSelect } from '@angular/material/select';
import { CommonUtilsService } from './../shared/common-utils.service';
import { UiControlType } from './../enums/ui-control-type.enum';
import { debounceTime, distinctUntilChanged, pairwise } from 'rxjs/operators';
import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  DoCheck,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';
import {
  FormGroup,
  FormArray,
  FormControl,
  Validators,
  AbstractControl,
} from '@angular/forms';
import { Config } from '../models/ui-config';
import {
  cloneDeep,
  difference,
  first,
  forEach,
  forOwn,
  isEqual,
  keys,
  values,
} from 'lodash';
import { Subscription } from 'rxjs';
import { OrderedListDragDropData } from '../models/ordered-list-drag-drop-data';
import { OrderedListDragDropPopupComponent } from '../shared/components/ordered-list-drag-drop-popup/ordered-list-drag-drop-popup.component';
import { AddNewDialogComponent } from '../add-new-dialog/add-new-dialog.component';
import { DEFAULT_DIALOG_CONFIG } from '../constants/lib-constants';
import { DynamicDictService } from '../shared/services/dynamic-dict.service';
import { ConfirmDialogComponent } from '../shared/components/confirm-dialog/confirm-dialog.component';
import { ConfirmDialogData } from '../models/confirm-dialog-data';
import { CompareFuncService } from '../shared/services/compare-func.service';

@Component({
  selector: 'ra-dynamic-dict',
  templateUrl: './dynamic-dict.component.html',
  styleUrls: ['./dynamic-dict.component.scss'],
  standalone: false,
})
export class DynamicDictComponent implements OnInit, OnDestroy, DoCheck {
  @Input() currentConfig: Config;
  @Input() parentFormGroup: FormGroup;
  @Input() form: FormGroup;
  uiControlType = UiControlType;
  filterCtrl = new FormControl('');
  searchTerm = '';
  customKey = 'dynamicKeys';
  keys = keys;
  dropdownCreateNewErrorMessage = [];
  id = CommonUtilsService.createGUID();
  dropdownCreateNewValidator: any;
  @Input() readOnly: boolean = false;
  showSpinnerForDropdown: boolean = false;
  get formArray() {
    const control = this.tempFormGroup?.get(this.customKey);
    return control instanceof FormArray ? control : null;
  }
  tempFormGroup: FormGroup;
  @ViewChild('matSelect') matSelect: MatSelect;
  compareValues: any;

  // Add readonly separator constant for multi-select keys
  private readonly MULTI_KEY_SEPARATOR = '|';
  // Composite-column separator used by cascade mode. Distinct from
  // ``MULTI_KEY_SEPARATOR`` so that multi-value-within-one-column ('A|B') and
  // multi-column ('North||Retail') can coexist on a single composite key.
  private readonly COMPOSITE_COL_SEPARATOR = '||';

  /**
   * When the dictKeyConfig has ``cascadeFromControl`` AND the referenced
   * sibling form control resolves to a non-empty array of column names, we
   * are in "cascade mode": each dict row renders N cascading dropdowns
   * (one per column), and the persisted dict key is the row's selected
   * values joined with ``||``. Empty array means no cascade — no rows are
   * rendered (matches the legacy "no categorical_vars" behavior).
   */
  cascadeLevels: Config[] = [];
  private cascadeFromControlSub: Subscription | null = null;

  /**
   * Per-cell ``FormControl`` cache for cascade-mode ``<mat-select>``s. The
   * row's selections live in a single ``FormControl<(string|string[])[]>``
   * inside ``formArray`` (one slot per cascade level), but
   * ``ngx-mat-select-search`` requires its host ``<mat-select>`` to be bound
   * via ``[formControl]`` / ``[ngModel]`` whenever ``multiple=true``. We
   * surface one synthetic ``FormControl`` per ``(rowIndex, levelIndex)``
   * pair, kept in sync with the underlying row value, so the template can
   * use ``[formControl]`` instead of ``[value]`` + ``(selectionChange)``.
   */
  private cascadeLevelControls = new Map<
    string,
    { control: FormControl; sub: Subscription }
  >();

  private keyOptionsPerRow: string[][] = [];
  private prevCombinedKeys: string[] = [];
  private isInsertingBlankRow = false;
  private isDeletingRow = false;

  /**
   * Inherited-keys mode (``dictKeyConfig.inheritKeysFrom``). Each entry is a
   * fixed (read-only) column whose name comes from another control's dict
   * value, and whose value FormControl persists this row's selections for
   * that column. ``optionConfig`` is a per-column clone of
   * ``dictValuesConfig.valueConfig`` so each column's fetched option list is
   * isolated (otherwise the shared ``values`` field would be overwritten by
   * whichever column was last opened).
   */
  inheritedColumns: {
    name: string;
    control: FormControl;
    optionConfig: Config;
    searchTerm: string;
  }[] = [];
  /** Signature of last-synced inherited columns - cheap change detection. */
  private lastInheritedColumnsKey = '';
  /** Saved dict captured at init so a reload's selections survive the swap
   *  to the inherited-keys-mode FormGroup. */
  private inheritedSeedValue: Record<string, string[]> = {};
  /** Per-row memoization for the single-select key dropdown's option list.
   *  Returning a fresh array every change-detection tick churns mat-select's
   *  ContentChildren (it sees a new option list, recomputes selection,
   *  re-emits, triggers another tick) — the loop is cheap individually but
   *  freezes the browser when it pulses every microtask. The cache keys on
   *  the base values reference plus a signature of every OTHER row's
   *  selected value, so a stable input set returns the SAME array. */
  private _keyOptionsCache: Map<
    number,
    { signature: string; base: any[]; result: any[] }
  > = new Map();
  /** Per-column memoization for the inherited-keys value multi-select's
   *  option list. Same rationale as ``_keyOptionsCache``: keep the option
   *  array reference stable when the inputs (base values, claimed values,
   *  own selection) haven't actually changed. */
  private _inheritedValueOptionsCache: Map<
    string,
    { base: any[]; takenSig: string; ownSig: string; result: any[] }
  > = new Map();

  get isDisabled(): boolean {
    return (
      this.readOnly ||
      !!this.parentFormGroup?.get(this.currentConfig?.property)?.disabled
    );
  }
  getSelectedValueTitle(
    formGroupOrControl:
      | FormGroup
      | FormControl
      | AbstractControl
      | null
      | undefined,
    config: Config | undefined,
  ) {
    if (!formGroupOrControl || !config) {
      return [];
    }
    return CommonUtilsService.getSelectedValue(
      formGroupOrControl as FormGroup | FormControl,
      config,
    );
  }

  values = values;

  asFormControl(control: AbstractControl): FormControl {
    return control as FormControl;
  }

  getCustomKeyControl(): FormControl | null {
    const control = this.tempFormGroup?.get(this.customKey);
    return control instanceof FormControl ? control : null;
  }

  getPropertyControl(): AbstractControl | null {
    const property = this.currentConfig?.property;
    return property ? (this.parentFormGroup?.get(property) ?? null) : null;
  }

  isParentPropertyInvalid(): boolean {
    const ctrl = this.getPropertyControl();
    return !!(ctrl?.invalid && ctrl?.touched);
  }

  getParentPropertyError(): string {
    const errs = this.getPropertyControl()?.errors;
    return errs ? String(values(errs)[0] ?? '') : '';
  }

  getCascadeColumnLabel(level: Config): string {
    return (
      (level as Config & { __cascadeColumn?: string }).__cascadeColumn ?? ''
    );
  }

  private syncCustomKeyValidationErrors(): void {
    const customKeyControl = this.tempFormGroup?.controls?.[this.customKey];
    if (this.isParentPropertyInvalid()) {
      customKeyControl?.setErrors({ error: 'true' });
    } else {
      customKeyControl?.setErrors(null);
    }
  }

  private subscriptions: Subscription[] = [];

  constructor(
    private commonUtils: CommonUtilsService,
    private jsonDynamicService: JsonDynamicComponentService,
    private dynamicDictService: DynamicDictService,
    private dialog: MatDialog,
    private compareFuncService: CompareFuncService,
    private cdr: ChangeDetectorRef,
  ) {}

  private ensureFormArray(): FormArray {
    if (!this.tempFormGroup) {
      this.tempFormGroup = new FormGroup({});
    }

    const existingControl = this.tempFormGroup.get(this.customKey);
    if (existingControl instanceof FormArray) {
      return existingControl;
    }

    const formArray = new FormArray([]);
    if (existingControl) {
      this.tempFormGroup.setControl(this.customKey, formArray);
    } else {
      this.tempFormGroup.addControl(this.customKey, formArray);
    }
    return formArray;
  }

  ngOnInit() {
    this.dropdownCreateNewValidator =
      this.dynamicDictService.dropdownCreateNewValidator;
    this.compareValues = this.compareFuncService.compareValues;
    this.jsonDynamicService.addDynamicDictControl(this.id, this);
    this.getValidDropdownValues();

    // Cascade mode takes priority over the legacy single-key /
    // multi-select flows when the dictKeyConfig opts in via cascadeFromControl
    // and the referenced sibling control has at least one column selected.
    if (this.currentConfig?.dictKeyConfig?.cascadeFromControl) {
      this.initCascadeFlow();
    } else if (this.currentConfig?.dictKeyConfig?.inheritKeysFrom) {
      // Inherited-keys mode: keys are LOCKED to another control's dict
      // value's keys; only the per-column value multi-select is editable.
      this.initInheritedKeysFlow();
    } else if (
      this.currentConfig?.dictKeyConfig?.isMultiSelect &&
      this.currentConfig?.dictKeyConfig?.uiControlType ===
        this.uiControlType.Dropdown &&
      this.currentConfig?.dictKeyConfig?.enableSelectAll
    ) {
      this.initSelectAllMultiDropdownFlow();
    } else {
      this.tempFormGroup = new FormGroup({
        [this.customKey]: this.currentConfig?.dictKeyConfig?.isMultiSelect
          ? new FormControl(null)
          : new FormArray([]),
      });
      this.subscriptions[this.subscriptions.length] = this.tempFormGroup
        .get(this.customKey)
        .valueChanges.pipe(
          debounceTime(500),
          distinctUntilChanged(),
          pairwise(),
        )
        .subscribe(([oldVals, newVals]) => {
          const currentFormGroup = (this.parentFormGroup.get(
            this.currentConfig.property,
          ) || new FormGroup({})) as FormGroup;
          let oldVal, newVal, removedValue;
          if (
            this.currentConfig?.dictKeyConfig?.uiControlType ===
              UiControlType.Dropdown &&
            this.currentConfig.dictKeyConfig.isMultiSelect
          ) {
            const valuesToAdd = difference(newVals, oldVals) || [];
            const valuesToRemove = difference(oldVals, newVals) || [];
            valuesToAdd.forEach((valueToAdd: any) =>
              this.addValue(valueToAdd, null, currentFormGroup),
            );
            valuesToRemove.forEach((valueToRemove: any) =>
              currentFormGroup.removeControl(valueToRemove),
            );
          } else if (oldVals.length === newVals.length) {
            oldVal = first(difference(oldVals, newVals)) as any;
            newVal = first(difference(newVals, oldVals)) as any;
            this.addValue(newVal, oldVal, currentFormGroup);
          } else if (oldVals.length > newVals.length) {
            removedValue = first(difference(oldVals, newVals)) as any;
            currentFormGroup.removeControl(removedValue);
          }
          const newFormGroup = new FormGroup({});
          forEach(newVals, (newValue) => {
            if (newValue != null && newValue !== '') {
              newFormGroup.addControl(newValue, currentFormGroup.get(newValue));
            }
          });
          this.updateFormGroup(newFormGroup);
          this.jsonDynamicService.emitFormValueChanged({
            formGroup: this.parentFormGroup,
            property: this.currentConfig.property,
            config: this.currentConfig,
            dictKey: newVal || removedValue,
            action: FormControlValueChangedAction.DictValueChanged,
          });
        });

      const dictValue = this.parentFormGroup?.get(
        this.currentConfig.property,
      )?.value;
      if (dictValue) {
        if (!this.currentConfig?.dictKeyConfig?.isMultiSelect) {
          forOwn(dictValue, (val, key) => {
            this.formArray.push(
              new FormControl(key, this.dropdownCreateNewValidator),
            );
          });
        } else {
          this.tempFormGroup?.get(this.customKey)?.setValue(keys(dictValue));
        }
      }
      this.registerFilterValueChangeSubscription();
    }
  }

  /**
   * Final safety net for cascade mode: every change-detection cycle, if we're
   * in cascade mode and the source columns are now resolvable but
   * ``cascadeLevels`` is still empty (e.g. the source-control valueChanges
   * subscription was never wired because the control didn't exist when
   * ``initCascadeFlow`` ran, or a downstream patch slipped past the debounced
   * subscription), heal the inconsistent state by rebuilding the levels.
   *
   * Without this, the user can land on a Breakpoints panel where neither the
   * cascade rows (gated on ``cascadeLevels.length > 0``) nor the "Add New"
   * button (gated on ``formArray.length === 0``) render — leaving them with
   * an unusable empty section and no way to add breakpoints.
   *
   * Cheap to run: bails immediately when not in cascade mode or when no
   * recovery is needed.
   */
  ngDoCheck(): void {
    this.ensureCascadeLevels();
    this.ensureInheritedKeysInSync();
    this.syncCustomKeyValidationErrors();
  }

  public isInheritedKeysMode(): boolean {
    return !!this.currentConfig?.dictKeyConfig?.inheritKeysFrom;
  }

  /**
   * Resolve the source control's dict-value keys (the inherited columns).
   * Returns ``[]`` when the source control isn't yet resolvable or its value
   * isn't a plain dict.
   */
  private getInheritedSourceColumns(): string[] {
    const path = this.currentConfig?.dictKeyConfig?.inheritKeysFrom;
    if (!path) return [];
    const sourceCtl =
      this.jsonDynamicService.mainFormGroup?.get(path) ||
      this.parentFormGroup?.get(path);
    const value = sourceCtl?.value;
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      return Object.keys(value).filter((c) => c != null && c !== '');
    }
    return [];
  }

  /**
   * Bootstrap inherited-keys mode. Attaches a ``FormGroup`` for the dict at
   * ``parentFormGroup[property]`` (preserving any existing one so reloads keep
   * the user's prior selections), then builds one ``FormControl`` per
   * currently-resolvable inherited column, seeded from the existing dict
   * value when present.
   */
  private initInheritedKeysFlow(): void {
    const property = this.currentConfig.property;
    const existing = this.parentFormGroup?.get(property);
    let dictGroup: FormGroup;
    if (existing instanceof FormGroup) {
      // Already a FormGroup (the common case: ``createFormControls`` seeded
      // it). Reuse it as-is — no need to ``setControl`` and trigger a parent
      // valueChanges emission (which can pulse change detection while the
      // surrounding ``ra-json-dynamic-form`` is mid-mount).
      dictGroup = existing;
    } else {
      const prior = existing?.value;
      if (prior && typeof prior === 'object' && !Array.isArray(prior)) {
        this.inheritedSeedValue = prior;
      }
      dictGroup = new FormGroup({});
      this.parentFormGroup?.setControl(property, dictGroup, {
        emitEvent: false,
      });
    }
    this.currentConfig.control = dictGroup;

    // tempFormGroup is unused in this mode but kept defined so generic
    // template hooks (parent error/touched checks, etc.) don't throw.
    if (!this.tempFormGroup) {
      this.tempFormGroup = new FormGroup({});
    }

    this.syncInheritedColumns();
    this.lastInheritedColumnsKey = this.getInheritedSourceColumns().join('|');
    // Wire up the shared mat-select-search ``filterCtrl`` so typing in the
    // value dropdown's search box updates ``searchTerm`` (consumed by the
    // ``filter`` pipe in the template).
    this.registerFilterValueChangeSubscription();
  }

  /**
   * Re-sync inherited columns when the source set changes. Mirrors the
   * ``inheritedSubsetValues`` defensive ``ngDoCheck`` pattern: cheap signature
   * comparison, bails unless the source columns actually changed.
   */
  private ensureInheritedKeysInSync(): void {
    if (!this.isInheritedKeysMode()) return;
    const key = this.getInheritedSourceColumns().join('|');
    if (key !== this.lastInheritedColumnsKey) {
      this.lastInheritedColumnsKey = key;
      this.syncInheritedColumns();
    }
  }

  /**
   * Reconcile ``inheritedColumns`` (and the dict FormGroup) with the current
   * inherited columns: keep surviving columns' selections, add new columns
   * with empty multi-select state, drop removed columns.
   */
  private syncInheritedColumns(): void {
    const wanted = this.getInheritedSourceColumns();
    const dictGroup = this.parentFormGroup?.get(
      this.currentConfig.property,
    ) as FormGroup;
    if (!dictGroup) return;

    // Drop controls for columns no longer present.
    for (const name of Object.keys(dictGroup.controls)) {
      if (!wanted.includes(name)) {
        dictGroup.removeControl(name, { emitEvent: false });
      }
    }

    const existingByName = new Map(
      this.inheritedColumns.map((c) => [c.name, c]),
    );
    this.inheritedColumns = wanted.map((name) => {
      const prior = existingByName.get(name);
      if (prior) return prior;

      const seed =
        (dictGroup.get(name)?.value as string[]) ||
        this.inheritedSeedValue[name] ||
        [];
      const control = new FormControl<string[]>(seed);
      dictGroup.setControl(name, control, { emitEvent: false });

      return {
        name,
        control,
        optionConfig: this.buildInheritedOptionConfig() as Config,
        searchTerm: '',
      };
    });
  }

  /** Per-column clone of ``dictValuesConfig.valueConfig`` so each column's
   *  fetched option list stays isolated. The column name itself is threaded
   *  to ``{{__key}}`` interpolations via ``rowContext`` in
   *  ``refreshInheritedValueOptions`` (so it wins over the parent
   *  ``ra-json-dynamic-form``'s ``dynamicDictKey`` inside
   *  ``getParamsSource``). */
  private buildInheritedOptionConfig(): Config {
    const template = this.currentConfig?.dictValuesConfig?.valueConfig;
    const clone = cloneDeep(template) as Config;
    clone.values = undefined;
    clone.allOptions = undefined;
    clone.optionsToShow = undefined;
    clone.reloadValues = true;
    return clone;
  }

  /**
   * Fetch values for an inherited column's multi-select. Triggered on
   * dropdown open. The per-column option config holds the resulting
   * ``values`` so columns don't overwrite each other.
   *
   * Threads the column name as ``__key`` so any ``{{__key}}`` interpolation
   * in the value config's ``valuesUrl`` / ``requestBody`` resolves to the
   * inherited column name (not the wrapping overlay row's model_ref). We
   * defend in three places because ``getParamsSource`` lets
   * ``dynamicDictService.key`` (set by the wrapping ``ra-json-dynamic-form``
   * to the model_ref) win over ``configValue?.__key``:
   *   1. Temporarily override ``dynamicDictService.key`` to the column name
   *      around the synchronous URL/body interpolation inside
   *      ``getDropdownValues`` (the ``new Promise(...)`` callback runs
   *      synchronously, so the swap is safe to revert immediately after the
   *      call returns — long before the async HTTP response).
   *   2. Put ``__key`` in ``formValue`` (configValue) as the primary
   *      fallback path.
   *   3. Pass ``rowContext`` with ``__key`` as the final, always-wins
   *      ``Object.assign`` override.
   */
  public refreshInheritedValueOptions(column: {
    name: string;
    optionConfig: Config;
  }): void {
    const cfg = column.optionConfig;
    cfg.loading = true;
    const ddSvc = this.commonUtils?.dynamicDictService;
    const priorKey = ddSvc?.key;
    if (ddSvc) ddSvc.key = column.name;
    let promise: Promise<unknown> | undefined;
    try {
      promise = this.commonUtils.getDropdownValues(
        cfg,
        Object.assign({}, this.form?.value || {}, { __key: column.name }),
        undefined,
        true,
        { __key: column.name },
      );
    } finally {
      // Synchronous URL/body interpolation has already happened inside
      // ``getDropdownValues`` by the time control returns here, so it is
      // safe to put the service back to whatever the wrapping
      // ``ra-json-dynamic-form`` had set.
      if (ddSvc) ddSvc.key = priorKey;
    }
    promise
      ?.then(() => {
        cfg.loading = false;
        this.cdr.detectChanges();
      })
      ?.catch(() => {
        cfg.loading = false;
      });
  }

  /**
   * Options for an inherited column's value multi-select with single-column
   * cross-row exclusivity applied (``valueConfig.excludeValuesClaimedBySiblings``).
   *
   * Mirrors ``inheritedSubsetValues.optionsFor``: only filter when exactly
   * one column is shared - with 2+ columns each row claims a value
   * COMBINATION, so the same value can recur across rows under a different
   * second-column value without ambiguity. This row's own current selection
   * is always preserved in the option list so the displayed chips stay
   * visible.
   */
  public getInheritedValueOptionsToShow(column: {
    name: string;
    control: FormControl;
    optionConfig: Config;
  }): any[] {
    const opts = (column.optionConfig?.values as any[]) || [];
    const path =
      this.currentConfig?.dictValuesConfig?.valueConfig
        ?.excludeValuesClaimedBySiblings;

    if (!path || this.inheritedColumns.length !== 1) {
      return opts;
    }
    const taken = this.collectValuesClaimedByOtherSources(column.name, path);
    const ownVals = ((column.control.value || []) as any[]).map((v) =>
      v?.toString(),
    );
    const takenSig = Array.from(taken).sort().join('|');
    const ownSig = [...ownVals].sort().join('|');
    const cached = this._inheritedValueOptionsCache.get(column.name);
    if (
      cached &&
      cached.base === opts &&
      cached.takenSig === takenSig &&
      cached.ownSig === ownSig
    ) {
      return cached.result;
    }
    const mine = new Set(ownVals);
    const result = opts.filter(
      (o) => !taken.has(o?.toString()) || mine.has(o?.toString()),
    );
    this._inheritedValueOptionsCache.set(column.name, {
      base: opts,
      takenSig,
      ownSig,
      result,
    });
    return result;
  }

  /**
   * Build the set of values for ``columnName`` already claimed by:
   *   1. The "current run's subset" dict at ``sourcePath`` (e.g.
   *      ``PRICE_PLUS.UPDATE_SUBSET_PARAMS.update_subset_cols_with_agg_cols``).
   *   2. SIBLING dict entries under the SAME outer dict FormGroup as this
   *      one - i.e. other overlay rows. Each sibling row stores a nested
   *      FormGroup whose own ``update_subset_cols_with_agg_cols`` dict
   *      holds the column's selections.
   *
   * Single-column-only callers (see ``getInheritedValueOptionsToShow``).
   */
  private collectValuesClaimedByOtherSources(
    columnName: string,
    sourcePath: string,
  ): Set<string> {
    const taken = new Set<string>();
    const add = (vals: unknown) => {
      if (Array.isArray(vals)) {
        vals.forEach((v) => v != null && taken.add(v.toString()));
      }
    };

    const sourceCtl =
      this.jsonDynamicService.mainFormGroup?.get(sourcePath) ||
      this.parentFormGroup?.get(sourcePath);
    add(sourceCtl?.value?.[columnName]);

    // Sibling overlay rows live under the same outer dict FormGroup as our
    // ``parentFormGroup`` (the row's nested-config FormGroup). Each sibling
    // is another row's nested-config FormGroup; pull its own
    // ``<property>[columnName]`` value.
    const outerDict = this.parentFormGroup?.parent;
    if (outerDict instanceof FormGroup) {
      for (const siblingKey of Object.keys(outerDict.controls)) {
        const sibling = outerDict.get(siblingKey);
        if (!sibling || sibling === this.parentFormGroup) continue;
        const dict = sibling.get?.(this.currentConfig.property);
        const v = dict?.value?.[columnName];
        add(v);
      }
    }
    return taken;
  }

  /** Form value for the multi-select chips title binding. */
  public getInheritedColumnTitle(column: {
    name: string;
    control: FormControl;
  }): string {
    const v = column.control.value;
    if (Array.isArray(v)) return v.join(', ');
    return v == null ? '' : String(v);
  }

  getValidDropdownValues() {
    if (this.currentConfig?.values) return;
    // Inherited-keys mode owns its own per-column value fetches via
    // ``refreshInheritedValueOptions`` (with the column name threaded as
    // ``rowContext.__key``). Letting the generic prefetch run here would call
    // the shared ``dictValuesConfig.valueConfig`` with ``__key`` set in
    // ``formValue``, but ``getParamsSource`` lets ``dynamicDictService.key``
    // (the parent ``ra-json-dynamic-form``'s ``dynamicDictKey``, e.g. the
    // overlay row's model_ref) win over ``configValue?.__key`` — so the
    // request body's ``column`` field would be the parent's dict key (a
    // model_ref) instead of the inherited column name. Skip the prefetch and
    // let the dropdown's own ``(opened)`` handler do the right thing.
    if (this.currentConfig?.dictKeyConfig?.inheritKeysFrom) return;
    const controlValue = this.currentConfig?.control?.value;
    if (!controlValue || !this.currentConfig?.dictValuesConfig?.valueConfig) {
      return;
    }
    // In read-only mode the value selections are rendered from the saved form
    // value, so prefetching the option lists is redundant. Keep fetching when
    // the value dropdown is keyed by `dropdownID`, which needs the options to
    // map ids to their display labels.
    if (
      this.readOnly &&
      !this.currentConfig.dictValuesConfig.valueConfig.dropdownID
    ) {
      return;
    }
    for (const param of Object.keys(controlValue)) {
      this.getDropdownValues(
        this.currentConfig.dictValuesConfig.valueConfig,
        this.matSelect,
        param,
      );
    }
  }

  private initSelectAllMultiDropdownFlow(): void {
    let dictGroup = this.parentFormGroup.get(
      this.currentConfig.property,
    ) as FormGroup;
    if (!dictGroup) {
      dictGroup = new FormGroup({});
      this.parentFormGroup.addControl(this.currentConfig.property, dictGroup);
    }

    this.tempFormGroup = new FormGroup({
      [this.customKey]: new FormArray([]),
    });

    const existing =
      this.parentFormGroup.get(this.currentConfig.property)?.value || {};
    const dictKeys: string[] = Object.keys(existing);
    this.prevCombinedKeys = dictKeys.slice();

    // Each row is an array representing a composite key; start with current split keys
    dictKeys.forEach((k) =>
      this.formArray.push(
        new FormControl(this.splitMultiKey(k), this.dropdownCreateNewValidator),
      ),
    );
    dictKeys.forEach((k) => {
      if (!dictGroup.get(k)) this.addValue(k, null, dictGroup);
    });

    this.multiSelectDictSubscription();
    this.registerFilterValueChangeSubscription();
  }

  public getCascadeColumns(): string[] {
    const ck = this.currentConfig?.dictKeyConfig;
    if (!ck?.cascadeFromControl) return [];
    const sourceCtl =
      this.parentFormGroup?.get(ck.cascadeFromControl) ||
      this.jsonDynamicService.mainFormGroup?.get(ck.cascadeFromControl);
    const v = sourceCtl?.value;
    if (Array.isArray(v)) return v.filter((c) => c != null && c !== '');
    if (typeof v === 'string' && v) return [v];
    return [];
  }

  /**
   * Clone ``dictKeyConfig`` once per cascade column, stripping the cascade
   * marker on each clone (so a clone isn't re-expanded) and forcing each
   * clone to refetch its values. Each clone is tagged with its column name
   * and level index — those land in the row-context at fetch time so the
   * config's ``valuesUrl`` / ``requestBody`` can interpolate them.
   */
  private buildCascadeLevels(columns: string[]): Config[] {
    const template = this.currentConfig?.dictKeyConfig;
    if (!template) return [];
    return columns.map((column, index) => {
      const clone = cloneDeep(template) as Config & {
        __cascadeColumn?: string;
        __cascadeIndex?: number;
      };
      // Strip the marker on the clone — only the OUTER template carries it.
      delete clone.cascadeFromControl;
      clone.__cascadeColumn = column;
      clone.__cascadeIndex = index;
      // Force re-fetch every time levels are rebuilt so stale option lists
      // from a different column don't leak in.
      clone.values = undefined;
      clone.allOptions = undefined;
      clone.optionsToShow = undefined;
      clone.reloadValues = true;
      return clone;
    });
  }

  /**
   * Bootstrap cascade mode: build levels from current column picks, hydrate
   * the FormArray from any saved composite-key dict, and subscribe to changes
   * in the source columns control so we rebuild when the user re-picks.
   */
  private initCascadeFlow(): void {
    let dictGroup = this.parentFormGroup.get(
      this.currentConfig.property,
    ) as FormGroup;
    if (!dictGroup) {
      dictGroup = new FormGroup({});
      this.parentFormGroup.addControl(this.currentConfig.property, dictGroup);
    }

    this.tempFormGroup = new FormGroup({
      [this.customKey]: new FormArray([]),
    });

    this.cascadeLevels = this.buildCascadeLevels(this.getCascadeColumns());

    // Hydrate from existing dict (composite ``a||b`` keys) when reopening a
    // saved config. Levels mismatch (e.g. saved 2-col config but only 1 col
    // currently picked) is handled by ``splitMultiKey`` truncation/padding.
    const existing =
      this.parentFormGroup.get(this.currentConfig.property)?.value || {};
    const dictKeys: string[] = Object.keys(existing);
    this.prevCombinedKeys = dictKeys.slice();
    dictKeys.forEach((k) =>
      this.formArray.push(
        new FormControl(this.splitMultiKey(k), this.dropdownCreateNewValidator),
      ),
    );
    dictKeys.forEach((k) => {
      if (!dictGroup.get(k)) this.addValue(k, null, dictGroup);
    });

    // Reuse the proven select-all subscription — it already handles rename /
    // add / remove based on the FormArray<FormControl<string[]>> shape, and
    // ``joinMultiKey`` returns the right ``||`` separator in cascade mode.
    this.multiSelectDictSubscription();
    this.registerFilterValueChangeSubscription();

    // React to the user changing which columns are selected: rebuild levels,
    // clear all rows (their selections are now stale), let them re-pick.
    //
    // CRITICAL: this must compare by *content* (deep equality on the column
    // list) — not by reference. ``patchValue`` calls during full-form
    // refreshes (e.g. forceUIRefresh after Generate Buckets) hand the same
    // array contents through but with a fresh reference, which would
    // otherwise wipe out the freshly-populated splits dict.
    this.subscribeToCascadeSourceChanges();

    // Defensive recovery: when ngOnInit runs DURING the parent form's initial
    // patch (e.g. opening a saved config), the source control's value may not
    // yet have propagated. Re-check on the next microtask so that
    // ``cascadeLevels`` reflects the freshly-patched columns even if the
    // valueChanges subscription missed the initial transition (or wasn't yet
    // wired because the source control didn't exist at this exact instant).
    Promise.resolve().then(() => this.ensureCascadeLevelsAndSubscription());
  }

  /**
   * Wire up the valueChanges subscription on the cascade source control. Idempotent —
   * does nothing if already subscribed or if the source control isn't resolvable.
   * Detects the "initial hydration" case (empty -> populated cols transition with
   * compatible existing rows) and rebuilds cascade levels WITHOUT wiping the rows.
   */
  private subscribeToCascadeSourceChanges(): void {
    if (this.cascadeFromControlSub) return;
    const ck = this.currentConfig?.dictKeyConfig;
    if (!ck?.cascadeFromControl) return;
    const sourceCtl =
      this.parentFormGroup?.get(ck.cascadeFromControl) ||
      this.jsonDynamicService.mainFormGroup?.get(ck.cascadeFromControl);
    if (!sourceCtl) return;
    let lastCols = this.getCascadeColumns();
    this.cascadeFromControlSub = sourceCtl.valueChanges
      .pipe(debounceTime(150))
      .subscribe(() => {
        const nextCols = this.getCascadeColumns();
        if (isEqual(nextCols, lastCols)) return;

        // "Initial hydration" guard: if ``lastCols`` was empty (source not
        // yet resolved at ngOnInit time) and the formArray already has rows
        // shaped to match the new columns count, this is the saved-state
        // patch propagating in — NOT the user picking different columns.
        // Rebuild levels but keep the hydrated rows / dict intact.
        const isHydratingExistingState =
          lastCols.length === 0 &&
          nextCols.length > 0 &&
          this.formArray.controls.length > 0 &&
          this.formArray.controls.every((c) => {
            const v = c.value;
            return Array.isArray(v) && v.length === nextCols.length;
          });

        lastCols = nextCols;

        if (isHydratingExistingState) {
          this.cascadeLevels = this.buildCascadeLevels(nextCols);
          this.cdr.detectChanges();
        } else {
          this.onCascadeColumnsChanged();
        }
      });
    this.subscriptions.push(this.cascadeFromControlSub);
  }

  /**
   * Defensive rebuild for the post-init race where ``cascadeLevels`` ended up
   * empty even though the source columns are now resolvable. Without this,
   * the template would render neither the cascade rows (gated on
   * ``cascadeLevels.length > 0``) nor the "Add New" button (gated on
   * ``formArray.length === 0``), leaving the user with an unusable empty
   * Breakpoints section.
   */
  private ensureCascadeLevelsAndSubscription(): void {
    if (!this.isCascadeMode()) return;
    // Make sure we have a subscription if the source control wasn't resolvable
    // at the original wiring point.
    this.subscribeToCascadeSourceChanges();
    const cur = this.getCascadeColumns();
    const haveCols = cur.length > 0;
    const haveLevels = this.cascadeLevels?.length > 0;
    if (haveCols && !haveLevels) {
      this.cascadeLevels = this.buildCascadeLevels(cur);
      this.cdr.detectChanges();
    }
  }

  private ensureCascadeLevels(): void {
    if (!this.isCascadeMode()) return;
    this.subscribeToCascadeSourceChanges();
    if (this.cascadeLevels?.length > 0) return;
    const cur = this.getCascadeColumns();
    if (cur.length === 0) return;
    this.cascadeLevels = this.buildCascadeLevels(cur);
    this.cdr.markForCheck();
  }

  public hasRenderableCascadeRows(): boolean {
    return !!(
      this.isCascadeMode() &&
      this.cascadeLevels?.length > 0 &&
      this.formArray?.controls?.length > 0
    );
  }

  public shouldShowCascadeInitialAddNew(): boolean {
    if (!this.isCascadeMode()) return false;
    return (
      (this.cascadeLevels?.length > 0 || this.getCascadeColumns().length > 0) &&
      !this.hasRenderableCascadeRows()
    );
  }

  public shouldShowCascadeEmptyHint(): boolean {
    return (
      this.isCascadeMode() &&
      !this.shouldShowCascadeInitialAddNew() &&
      this.getCascadeColumns().length === 0 &&
      !this.hasRenderableCascadeRows()
    );
  }

  public addInitialCascadeRow(): void {
    if (this.isCascadeMode()) {
      this.ensureCascadeLevels();
      if (this.hasRenderableCascadeRows()) {
        this.cdr.detectChanges();
        return;
      }
    }
    this.addRow((this.formArray?.controls?.length || 0) - 1);
  }

  /**
   * Rebuild cascade levels and drop any stale row selections / dict entries
   * when the user changes which columns drive the cascade.
   */
  private onCascadeColumnsChanged(): void {
    const newCols = this.getCascadeColumns();
    this.cascadeLevels = this.buildCascadeLevels(newCols);

    const dictGroup = this.parentFormGroup.get(
      this.currentConfig.property,
    ) as FormGroup;
    if (dictGroup) {
      Object.keys(dictGroup.controls).forEach((k) =>
        dictGroup.removeControl(k),
      );
    }
    this.formArray.clear();
    this.prevCombinedKeys = [];
    this.resetCascadeLevelControlsCache();
    this.cdr.detectChanges();
  }

  /**
   * Fetch unique values for a single cascade level for a specific row, using
   * the row's prior selections as filters. Called both on opening a dropdown
   * and after an upstream level change.
   *
   * In multi-select-per-level mode (``dictKeyConfig.isMultiSelect``), each
   * upstream selection is an array; we forward that array verbatim so the
   * backend can apply an ``IN (...)`` filter rather than equality.
   */
  public refreshCascadeLevelValues(rowIndex: number, levelIndex: number): void {
    const level = this.cascadeLevels[levelIndex];
    if (!level) return;

    if (
      levelIndex === 0 &&
      Array.isArray(level.values) &&
      level.values.length > 0
    ) {
      return;
    }

    const rowVals = (this.formArray.at(rowIndex)?.value || []) as (
      | string
      | string[]
    )[];
    const filters: { [col: string]: string | string[] } = {};
    for (let i = 0; i < levelIndex; i++) {
      const colName = (this.cascadeLevels[i] as any).__cascadeColumn;
      if (!colName) continue;
      const v = rowVals[i];
      if (Array.isArray(v)) {
        const cleaned = v.filter((x) => x != null && x !== '');
        if (cleaned.length) filters[colName] = cleaned;
      } else if (v != null && v !== '') {
        filters[colName] = v;
      }
    }
    const rowContext = {
      __cascadeColumn: (level as any).__cascadeColumn,
      __cascadeIndex: levelIndex,
      __cascadeFilters: filters,
    };
    // Clear cached values to force a refetch — cascading filters on prior
    // selections must produce a fresh option list, not a cached one.
    level.values = undefined;
    level.reloadValues = true;
    this.commonUtils
      .getDropdownValues(
        level,
        Object.assign({}, this.form?.value || {}),
        undefined,
        true,
        rowContext,
      )
      .then(() => this.cdr.detectChanges());
  }

  /**
   * Fired by the template when the user changes a cascade-level selection in
   * row ``rowIndex`` at level ``levelIndex``. Clears all downstream level
   * selections (they no longer make sense under the new upstream value) and
   * triggers their value lists to be refetched on next open.
   */
  public onCascadeLevelChanged(rowIndex: number, levelIndex: number): void {
    const ctl = this.formArray.at(rowIndex) as FormControl;
    const cur = ((ctl.value || []) as (string | string[])[]).slice();
    const blank: string | string[] = this.isLevelMultiSelect() ? [] : '';
    let mutated = false;
    for (let i = levelIndex + 1; i < this.cascadeLevels.length; i++) {
      if (!this.isCascadeLevelEmpty(cur[i])) {
        cur[i] = Array.isArray(blank) ? [] : '';
        mutated = true;
      }
    }
    if (mutated) ctl.setValue(cur);
  }

  public isCascadeMode(): boolean {
    return !!this.currentConfig?.dictKeyConfig?.cascadeFromControl;
  }

  /**
   * When ``true`` each cascade-level dropdown renders as a multi-select and
   * the row's per-level slot is a ``string[]`` instead of a ``string``. The
   * persisted dict key encodes per-level arrays via ``MULTI_KEY_SEPARATOR``
   * (``|``) within a level and ``COMPOSITE_COL_SEPARATOR`` (``||``) between
   * levels — e.g. ``"110|122||AWNING|BOAT TRAILERS"``.
   */
  public isLevelMultiSelect(): boolean {
    return !!(
      this.isCascadeMode() && this.currentConfig?.dictKeyConfig?.isMultiSelect
    );
  }

  private isCascadeLevelEmpty(v: string | string[]): boolean {
    if (Array.isArray(v)) return v.length === 0;
    return v == null || v === '';
  }

  /** Build a fresh blank row sized to ``cascadeLevels.length`` with the
   * right slot shape for the current mode (``''`` per slot in single-select
   * cascade, ``[]`` per slot in multi-select-per-level cascade). */
  private makeBlankCascadeRow(): (string | string[])[] {
    this.ensureCascadeLevels();
    const n = this.cascadeLevels.length;
    const out: (string | string[])[] = [];
    for (let i = 0; i < n; i++) out.push(this.isLevelMultiSelect() ? [] : '');
    return out;
  }

  /**
   * Returns the row's selection at a given level. In multi-select-per-level
   * mode this is always a ``string[]`` (mat-select multi expects an array);
   * otherwise it's the legacy single ``string``.
   */
  public getCascadeLevelValue(
    rowIndex: number,
    levelIndex: number,
  ): string | string[] {
    const arr = (this.formArray.at(rowIndex)?.value || []) as (
      | string
      | string[]
    )[];
    const v = arr[levelIndex];
    if (this.isLevelMultiSelect()) {
      if (Array.isArray(v)) return v;
      if (v == null || v === '') return [];
      return [v];
    }
    if (Array.isArray(v)) return v[0] ?? '';
    return v ?? '';
  }

  /** Convenience for template ``[disabled]`` checks that need to treat an
   * empty array (multi-select) and ``''`` (single) the same way. */
  public hasCascadeLevelValue(rowIndex: number, levelIndex: number): boolean {
    return !this.isCascadeLevelEmpty(
      this.getCascadeLevelValue(rowIndex, levelIndex),
    );
  }

  /** Always-string view of a cascade-level value for template ``[title]`` /
   * tooltip bindings. Joins multi-selected values with ``, `` so the browser
   * tooltip stays readable. */
  public getCascadeLevelValueTitle(
    rowIndex: number,
    levelIndex: number,
  ): string {
    const v = this.getCascadeLevelValue(rowIndex, levelIndex);
    if (Array.isArray(v)) return v.join(', ');
    return v ?? '';
  }

  /** Always-array view of a cascade-level value for template iteration
   * (``@for`` loops over the chips inside a ``mat-select-trigger``). */
  public getCascadeLevelValueArray(
    rowIndex: number,
    levelIndex: number,
  ): string[] {
    const v = this.getCascadeLevelValue(rowIndex, levelIndex);
    if (Array.isArray(v)) return v;
    if (v == null || v === '') return [];
    return [v];
  }

  public setCascadeLevelValue(
    rowIndex: number,
    levelIndex: number,
    value: string | string[],
  ): void {
    const ctl = this.formArray.at(rowIndex) as FormControl;
    const cur = ((ctl.value || []) as (string | string[])[]).slice();
    const padBlank: string | string[] = this.isLevelMultiSelect() ? [] : '';
    while (cur.length < this.cascadeLevels.length)
      cur.push(Array.isArray(padBlank) ? [] : '');
    if (this.isLevelMultiSelect()) {
      if (Array.isArray(value)) {
        cur[levelIndex] = value.filter((v) => v != null && v !== '');
      } else if (value == null || value === '') {
        cur[levelIndex] = [];
      } else {
        cur[levelIndex] = [value];
      }
    } else {
      cur[levelIndex] = Array.isArray(value) ? (value[0] ?? '') : (value ?? '');
    }
    ctl.setValue(cur);
  }

  /**
   * Returns the synthetic per-cell ``FormControl`` for a cascade ``mat-select``,
   * creating and caching it (plus its writeback subscription) on first
   * access. Required so ``ngx-mat-select-search`` sees a ``[formControl]``
   * binding when ``multiple=true``. The control is always re-synced from
   * the row's authoritative value via ``{ emitEvent: false }`` to avoid
   * feedback loops with the writeback subscription below.
   */
  public getCascadeLevelControl(
    rowIndex: number,
    levelIndex: number,
  ): FormControl {
    const key = `${rowIndex}:${levelIndex}`;
    const current = this.getCascadeLevelValue(rowIndex, levelIndex);
    const cached = this.cascadeLevelControls.get(key);
    if (cached) {
      if (!isEqual(cached.control.value, current)) {
        cached.control.setValue(current, { emitEvent: false });
      }
      return cached.control;
    }
    // Cascade prerequisite: any level > 0 must wait until the immediately
    // upstream level has a value before it can be opened. Template
    // ``[disabled]`` is ignored when a control is bound via ``[formControl]``
    // (Angular reactive forms only honor ``FormControl.disable()``), so we
    // must seed the disabled state directly on the synthetic control.
    const prereqMissing = this.isCascadeLevelPrereqMissing(
      rowIndex,
      levelIndex,
    );
    const control = new FormControl({
      value: current,
      disabled: prereqMissing,
    });
    const sub = control.valueChanges.subscribe((val) => {
      this.setCascadeLevelValue(rowIndex, levelIndex, val);
      this.onCascadeLevelChanged(rowIndex, levelIndex);
      this.syncCascadeLevelControlsFromRow(rowIndex);
      // Picking / clearing this level changes the prerequisite for every
      // downstream level, so re-evaluate their disabled state.
      this.syncCascadeLevelDisabledStates(rowIndex);
    });
    this.cascadeLevelControls.set(key, { control, sub });
    return control;
  }

  /** ``true`` when this cascade slot is downstream of an empty upstream
   * slot — i.e. the user hasn't yet picked the value(s) needed to filter
   * this level's options. Level 0 is never prerequisite-missing. */
  private isCascadeLevelPrereqMissing(
    rowIndex: number,
    levelIndex: number,
  ): boolean {
    return (
      levelIndex > 0 && !this.hasCascadeLevelValue(rowIndex, levelIndex - 1)
    );
  }

  /** Walk every cached synthetic control for ``rowIndex`` and bring its
   * ``disabled`` state in line with the current prerequisite. Called from
   * the synthetic-control writeback after the user picks / clears any
   * cascade level. ``{ emitEvent: false }`` prevents the status change
   * from re-entering the writeback subscription. */
  private syncCascadeLevelDisabledStates(rowIndex: number): void {
    for (let li = 0; li < this.cascadeLevels.length; li++) {
      const key = `${rowIndex}:${li}`;
      const cached = this.cascadeLevelControls.get(key);
      if (!cached) continue;
      const shouldDisable = this.isCascadeLevelPrereqMissing(rowIndex, li);
      if (shouldDisable && !cached.control.disabled) {
        cached.control.disable({ emitEvent: false });
      } else if (!shouldDisable && cached.control.disabled) {
        cached.control.enable({ emitEvent: false });
      }
    }
  }

  /** Refreshes every cached per-cell ``FormControl`` for the given row from
   * the row's authoritative value (used after ``onCascadeLevelChanged`` clears
   * downstream slots, so the UI reflects the cleared state). Emits no events
   * to avoid re-entering the writeback subscription. */
  private syncCascadeLevelControlsFromRow(rowIndex: number): void {
    for (let li = 0; li < this.cascadeLevels.length; li++) {
      const key = `${rowIndex}:${li}`;
      const cached = this.cascadeLevelControls.get(key);
      if (!cached) continue;
      const expected = this.getCascadeLevelValue(rowIndex, li);
      if (!isEqual(cached.control.value, expected)) {
        cached.control.setValue(expected, { emitEvent: false });
      }
    }
  }

  /** Tear down every cached per-cell ``FormControl`` and its subscription.
   * Called whenever row indices shift (rows added/removed/cleared) so we
   * don't keep stale controls bound to the wrong ``(rowIndex, levelIndex)``
   * pair. The next template pass will lazily recreate what it needs. */
  private resetCascadeLevelControlsCache(): void {
    this.cascadeLevelControls.forEach((entry) => entry.sub.unsubscribe());
    this.cascadeLevelControls.clear();
  }

  // Keeps dictGroup in sync with FormArray rows; preserves values; guarded cleanup; emits events
  private multiSelectDictSubscription(): void {
    this.subscriptions.push(
      this.formArray.valueChanges
        .pipe(debounceTime(200), distinctUntilChanged())
        .subscribe((rows: any[]) => {
          if (!Array.isArray(rows)) return;

          const dictGroup = this.parentFormGroup.get(
            this.currentConfig.property,
          ) as FormGroup;

          // Build current and previous combined keys
          const combinedKeys: string[] = rows.map((r) =>
            this.joinMultiKey(r || []),
          );
          const prev = (this.prevCombinedKeys || []).slice();
          this.prevCombinedKeys = combinedKeys;

          // Per-row: handle rename, add, remove (preserve values on rename)
          rows.forEach((row, idx) => {
            const newKey = this.joinMultiKey(row || []);
            const oldKey = prev[idx] || null;

            if (!newKey) {
              // Only remove oldKey if no OTHER row still references it.
              // Without this check, transitioning a duplicate row back to
              // an incomplete state would yank the FormGroup out from
              // under the row(s) still using it.
              if (
                oldKey &&
                dictGroup.get(oldKey) &&
                !combinedKeys.some((k, i) => i !== idx && k === oldKey)
              )
                dictGroup.removeControl(oldKey);
              return;
            }
            if (oldKey && oldKey !== newKey) {
              const oldCtl = dictGroup.get(oldKey);
              if (oldCtl) {
                // Only MOVE the existing FormGroup from oldKey to newKey
                // when oldKey is not still referenced by another row in
                // the current snapshot. The shared-key case (e.g. a user
                // resolving a duplicate cascade row by changing one of
                // two rows that both pointed at oldKey) must NOT yank
                // the shared FormGroup away — Row 0 still depends on it.
                // Falls through to ``addValue`` below so newKey gets a
                // fresh FormGroup; the inner ``<ra-json-dynamic-form>``
                // remounts (its template ``@if`` was unmounted while
                // newKey had no FormGroup) and its ``ngOnInit`` attaches
                // the inner controls (splits FormArray) on the fresh
                // FormGroup.
                const oldKeyStillUsed = combinedKeys.some(
                  (k, i) => i !== idx && k === oldKey,
                );
                if (!oldKeyStillUsed) {
                  dictGroup.removeControl(oldKey);
                  if (!dictGroup.get(newKey)) {
                    if (this.currentConfig?.resetDictValuesOnKeyChange) {
                      this.addValue(newKey, null, dictGroup);
                    } else {
                      dictGroup.addControl(newKey, oldCtl);
                    }
                  }
                  return;
                }
                // Fall through: oldKey is still in use, so leave it
                // alone and create a fresh FormGroup for newKey below.
              }
            }

            if (!dictGroup.get(newKey)) this.addValue(newKey, null, dictGroup);
          });

          // Cleanup: remove any controls not present in combinedKeys
          Object.keys(dictGroup.controls)
            .filter((k) => !combinedKeys.includes(k))
            .forEach((k) => dictGroup.removeControl(k));

          // Optional validator pass from parent control
          const parentControl = this.parentFormGroup.get(
            this.currentConfig.property,
          );
          if (parentControl && (parentControl as any).validators) {
            dictGroup.setValidators((parentControl as any).validators);
          }
          dictGroup.updateValueAndValidity({ emitEvent: false });

          // Refresh any key options (e.g., uniqueness constraints) and emit
          this.refreshAllKeyOptionsForMultiSelect();
          this.updateCascadeDuplicateRowErrors(rows, combinedKeys);
          this.jsonDynamicService.emitFormValueChanged({
            formGroup: this.parentFormGroup,
            property: this.currentConfig.property,
            config: this.currentConfig,
            dictKey: combinedKeys[combinedKeys.length - 1],
            action: FormControlValueChangedAction.DictValueChanged,
          });

          if (this.isInsertingBlankRow || this.isDeletingRow) {
            Promise.resolve().then(() => {
              this.isInsertingBlankRow = false;
              this.isDeletingRow = false;
            });
          }
        }),
    );
  }

  private addValue(
    newVal: string,
    oldVal: string,
    currentFormGroup: FormGroup,
  ) {
    let oldValControl;
    if (oldVal != null) {
      oldValControl = this.currentConfig?.resetDictValuesOnKeyChange
        ? null
        : currentFormGroup.get(oldVal);
      currentFormGroup.removeControl(oldVal);
    }
    if (newVal != null && newVal !== '') {
      if (this.currentConfig?.dictValuesConfig?.valueConfig != null) {
        const isRequired =
          this.currentConfig?.dictValuesConfig?.valueConfig?.validators
            ?.required || false;
        const formControl =
          oldValControl ||
          new FormControl(null, isRequired ? Validators.required : null);
        currentFormGroup.addControl(newVal, formControl);
      } else if (this.currentConfig?.dictValuesConfig?.valueDefault != null) {
        const formControl: FormControl = oldValControl || new FormControl(null);
        const value = this.commonUtils.getInterpolatedValue(
          this.currentConfig.dictValuesConfig.valueDefault,
          { __key: newVal },
        );
        formControl.setValue(value);
        currentFormGroup.addControl(newVal, formControl);
      } else if (this.currentConfig?.dictValuesConfig?.config) {
        const formGroup = oldValControl || new FormGroup({});
        currentFormGroup.addControl(newVal, formGroup);
      }
    }
  }

  private updateFormGroup(newFormGroup: FormGroup) {
    const control = this.parentFormGroup.get(this.currentConfig.property);
    this.parentFormGroup.removeControl(this.currentConfig.property);
    newFormGroup.addValidators(control['_rawValidators']);
    this.parentFormGroup.addControl(this.currentConfig.property, newFormGroup);
    this.currentConfig.control = newFormGroup;
  }

  registerFilterValueChangeSubscription() {
    this.subscriptions[this.subscriptions.length] = this.filterCtrl.valueChanges
      .pipe(debounceTime(250), distinctUntilChanged())
      .subscribe((value) => {
        this.searchTerm = value;
      });
  }

  getValueFormGroup(key: string) {
    if (this.currentConfig?.dictValuesConfig?.valueConfig) {
      return new FormGroup({});
    }

    const dictGroup = this.parentFormGroup?.get(
      this.currentConfig.property?.toString(),
    ) as FormGroup;
    if (!dictGroup || key == null || key === '') {
      return null;
    }

    // IMPORTANT: do NOT eagerly create-and-attach an empty FormGroup here for
    // missing keys. ``multiSelectDictSubscription`` is the sole owner of the
    // dictGroup's child FormGroups and must be allowed to either create them
    // fresh (via ``addValue``) or migrate them across renames without
    // racing against this template-side getter. Eager creation used to
    // produce a corrupted state when the user resolved a duplicate cascade
    // row: the eager empty FormGroup for the new key would prevent the
    // subscription's "move oldKey -> newKey" branch from re-attaching the
    // existing controls (splits FormArray, etc.), leaving the inner
    // ``<ra-json-dynamic-form>`` bound to a stale FormGroup reference and
    // throwing ``Cannot find control with path: 'splits' -> 0``. Returning
    // ``null`` lets the template's ``@if`` guard unmount the inner form
    // cleanly; once the subscription creates a real FormGroup ~200ms later
    // the inner form remounts and its ``ngOnInit`` correctly registers the
    // child controls (splits FormArray) on the new FormGroup.
    return (dictGroup.get(key.toString()) as FormGroup) || null;
  }

  getValueFormControl(key: string) {
    return this.parentFormGroup
      ?.get(this.currentConfig.property?.toString())
      ?.get(key?.toString()) as FormControl;
  }

  add(i: number) {
    const formArray = this.ensureFormArray();
    if (this.isCascadeMode()) {
      const blank: (string | string[])[] = this.makeBlankCascadeRow();
      formArray.insert(
        i + 1,
        new FormControl(blank, this.dropdownCreateNewValidator),
      );
      this.prevCombinedKeys.splice(i + 1, 0, '');
    } else {
      formArray.insert(
        i + 1,
        new FormControl(null, this.dropdownCreateNewValidator),
      );
    }
    // this.parentFormGroup?.get(this.currentConfig.property?.toString())?.markAsTouched(); //// might come handy in future
    this.dropdownCreateNewErrorMessage.splice(i + 1, 0, '');
    // Row count changed: blow the per-row option cache so subsequent lookups
    // build a fresh array (and signature) for every row index.
    this._keyOptionsCache.clear();
  }

  remove(i: number) {
    this.ensureFormArray().removeAt(i);
    this._keyOptionsCache.clear();
    // this.parentFormGroup?.get(this.currentConfig.property?.toString())?.markAsTouched(); //// might come handy in future
    this.dropdownCreateNewErrorMessage.splice(i, 1);
    if (this.isCascadeMode()) this.resetCascadeLevelControlsCache();
  }

  public addRow(i: number) {
    if (this.isDisabled) return;
    this.isInsertingBlankRow = true;
    const formArray = this.ensureFormArray();

    // Cascade mode shares the array-shaped FormControl with select-all
    // multi-select mode (one ``string[]`` per row), but with a fixed length
    // equal to the number of cascade levels rather than the user's variadic
    // multi-select picks. In multi-select-per-level cascade mode each slot
    // is itself a ``string[]`` (i.e. the row is ``string[][]``).
    if (this.isCascadeMode()) {
      const blank: (string | string[])[] = this.makeBlankCascadeRow();
      formArray.insert(
        i + 1,
        new FormControl(blank, this.dropdownCreateNewValidator),
      );
      this.prevCombinedKeys.splice(i + 1, 0, '');
      this.resetCascadeLevelControlsCache();
    } else if (this.currentConfig?.dictKeyConfig?.isMultiSelect) {
      formArray.insert(
        i + 1,
        new FormControl([], this.dropdownCreateNewValidator),
      );
      this.prevCombinedKeys.splice(i + 1, 0, '');
      this.refreshAllKeyOptionsForMultiSelect();
    } else {
      formArray.insert(
        i + 1,
        new FormControl(null, this.dropdownCreateNewValidator),
      );
    }
    this.dropdownCreateNewErrorMessage.splice(i + 1, 0, '');
  }

  public deleteRow(i: number) {
    if (this.isDisabled) return;
    this.isDeletingRow = true;
    const formArray = this.ensureFormArray();
    const dictGroup = this.parentFormGroup?.get(
      this.currentConfig.property,
    ) as FormGroup;

    if (
      this.isCascadeMode() ||
      this.currentConfig?.dictKeyConfig?.isMultiSelect
    ) {
      const selArr = (formArray.at(i)?.value || []) as (string | string[])[];
      const combinedKey = this.joinMultiKey(selArr);
      if (combinedKey && dictGroup?.get(combinedKey))
        dictGroup.removeControl(combinedKey);
      this.prevCombinedKeys.splice(i, 1);
    } else {
      const keyToRemove = formArray.at(i)?.value;
      if (keyToRemove && dictGroup?.get(keyToRemove))
        dictGroup.removeControl(keyToRemove);
    }

    formArray.removeAt(i);
    this.dropdownCreateNewErrorMessage.splice(i, 1);
    this.refreshAllKeyOptionsForMultiSelect();
    if (this.isCascadeMode()) this.resetCascadeLevelControlsCache();
  }

  deleteControl(controlName) {
    let startIndex = 0;
    let indices: number[] = [];
    while (startIndex < this.formArray.controls.length) {
      let indx = this.formArray.controls.findIndex(
        (control, i) => i >= startIndex && control.value === controlName,
      );
      if (indx !== -1) {
        indices.push(indx);
        startIndex = indx + 1;
      } else {
        break;
      }
    }
    indices.sort((a, b) => b - a);
    for (const i of indices) {
      if (i >= 0 && i < this.formArray.controls.length) {
        this.remove(i);
      }
    }
  }

  getDropdownValues(config: Config, matSelect?: MatSelect, key: string = null) {
    if (matSelect) {
      this.matSelect = matSelect;
    }
    this.commonUtils
      .getDropdownValues(
        config,
        Object.assign({}, this.form?.value || {}, { __key: key }),
        this.matSelect,
        false,
      )
      .then((_response) => {
        if (this.currentConfig.dictValuesConfig?.valueConfig?.values) {
          let validValues =
            this.currentConfig.dictValuesConfig.valueConfig.values.map((v) =>
              String(v),
            );
          const selectedVals = this.parentFormGroup
            .get(this.currentConfig.property)
            .get(key).value;
          const validSelectedValues = selectedVals.filter((v) =>
            validValues.includes(v),
          );
          if (validSelectedValues.length !== selectedVals.length) {
            this.parentFormGroup
              ?.get(this.currentConfig.property)
              ?.get(key)
              ?.setValue(
                validSelectedValues.length ? validSelectedValues : null,
              );
            this.cdr.detectChanges();
          }
        }
      });
  }

  toggleDragDropControl() {
    if (this.currentConfig?.dictKeyConfig?.enableSelectAll) return;
    const anyKeyEmpty = this.formArray?.value?.some(
      (val) => val == null || val === '',
    );
    if (anyKeyEmpty) {
      this.dialog.open(ConfirmDialogComponent, {
        ...DEFAULT_DIALOG_CONFIG,
        data: {
          title: 'Warning',
          message: 'Please fill all the keys before changing the order',
          showCancelBtn: false,
        } as ConfirmDialogData,
      });
      return;
    }

    this.dialog.open(OrderedListDragDropPopupComponent, {
      width: 'auto',
      minHeight: '350px',
      data: {
        topDirectionText: this.currentConfig?.topDirectionText,
        bottomDirectionText: this.currentConfig?.bottomDirectionText,
        topDirectionColor: this.currentConfig?.topDirectionColor,
        bottomDirectionColor: this.currentConfig?.bottomDirectionColor,
        allValues: this.currentConfig?.dictKeyConfig?.values,
        list: this.formArray?.value,
        id: this.currentConfig?.dictKeyConfig?.dropdownID,
        label: this.currentConfig?.dictKeyConfig?.dropdownLabel,
        orderChanged: this.onOrderChanged.bind(this),
      } as OrderedListDragDropData,
    });
  }

  onOrderChanged(values: string[]) {
    const formArrayMap = this.formArray.controls.reduce((prev, curr) => {
      prev[curr.value] = curr;
      return prev;
    }, {});

    const formGroup = this.parentFormGroup.get(this.currentConfig.property);
    const newFormGroup = new FormGroup({});
    forEach(values, (val, index) => {
      this.formArray.removeAt(index);
      this.formArray.insert(index, formArrayMap[val]);
      newFormGroup.addControl(val, formGroup.get(val));
    });
    this.updateFormGroup(newFormGroup);
  }

  openAddNewDialog(i: number, matSelect: MatSelect) {
    const dialogRef = this.dialog.open(AddNewDialogComponent, {
      ...DEFAULT_DIALOG_CONFIG,
    });

    dialogRef.afterClosed().subscribe((res) => {
      if (res) {
        this.matSelect = matSelect;
        this.matSelect.value = res;
        this.getDropdownValues(this.currentConfig?.dictKeyConfig);
        this.dropdownCreateNewErrorMessage[i] =
          'Please select a key from dropdown.';
      } else {
        this.dropdownCreateNewErrorMessage[i] = 'Please review the field.';
      }
    });
  }

  async validateDropdownSelectedValues(
    configFilter: Function,
    hasModelChanged: boolean,
    formValueWithDefaults?: any,
  ): Promise<boolean> {
    const result = await Promise.all([
      this.validateSelectedValuesForKeyControl(
        configFilter,
        hasModelChanged,
        formValueWithDefaults,
      ),
      this.validateSelectedValuesForValueControl(
        configFilter,
        hasModelChanged,
        formValueWithDefaults,
      ),
    ]);
    return result.every((res) => res);
  }

  private async refreshDropdownValues(
    config: Config,
    formGroupOrControl: AbstractControl,
    hasModelChanged: boolean,
    key: string = null,
    formValueWithDefaults?: any,
  ): Promise<void> {
    try {
      this.showSpinnerForDropdown = true;
      let redundantSelectedValues;
      let currentSelectedValues;
      if (key) {
        currentSelectedValues = this.commonUtils.findValueByKey(
          formValueWithDefaults,
          key,
        );
        if (
          currentSelectedValues == null &&
          formGroupOrControl instanceof FormControl
        ) {
          currentSelectedValues = formGroupOrControl.value;
        }
      } else {
        currentSelectedValues =
          formGroupOrControl instanceof FormControl
            ? formGroupOrControl?.value
            : keys(formGroupOrControl?.value);
      }
      await this.commonUtils.getDropdownValues(
        config,
        Object.assign({}, this.form?.value || {}, { __key: key }),
        this.matSelect,
        hasModelChanged,
      );
      const isMultiSelect = config.isMultiSelect;
      if (isMultiSelect || typeof currentSelectedValues === 'object') {
        redundantSelectedValues = currentSelectedValues || [];
        redundantSelectedValues = redundantSelectedValues.filter(
          (val: any) =>
            !config.values?.map((v) => String(v)).includes(String(val)),
        );
        currentSelectedValues = difference(
          currentSelectedValues,
          redundantSelectedValues,
        );
      } else {
        redundantSelectedValues = !config.values?.includes(
          currentSelectedValues,
        )
          ? currentSelectedValues
          : null;
        if (redundantSelectedValues) currentSelectedValues = null;
      }
      if (formGroupOrControl instanceof FormControl) {
        if (currentSelectedValues !== null) {
          formGroupOrControl?.setValue(currentSelectedValues, {
            emitEvent: false,
          });
        }
      } else if (formGroupOrControl instanceof FormGroup) {
        redundantSelectedValues =
          !isMultiSelect && typeof redundantSelectedValues === 'string'
            ? [redundantSelectedValues]
            : redundantSelectedValues;
        (redundantSelectedValues as string[]).forEach((val) => {
          formGroupOrControl?.removeControl(val, { emitEvent: false });
        });
        if (this.tempFormGroup?.get(this.customKey) instanceof FormArray) {
          (redundantSelectedValues as string[]).forEach((invalidColumn) => {
            const idx = this.tempFormGroup
              ?.get(this.customKey)
              ?.value.findIndex((val) => val === invalidColumn);
            (this.tempFormGroup?.get(this.customKey) as FormArray)?.removeAt(
              idx,
              { emitEvent: false },
            );
          });
        }
        this.tempFormGroup
          ?.get(this.customKey)
          ?.setValue(currentSelectedValues, { emitEvent: false });
      }
      if (isMultiSelect && config.selectAllOnInit) {
        if (formGroupOrControl instanceof FormControl) {
          formGroupOrControl?.setValue(config.values, { emitEvent: false });
        } else {
          formGroupOrControl
            ?.get(config.property)
            ?.setValue(config.values, { emitEvent: false });
        }
      }
    } finally {
      this.showSpinnerForDropdown = false;
    }
  }

  private async validateSelectedValuesForKeyControl(
    configFilter: Function,
    hasModelChanged: boolean,
    formValueWithDefaults: any,
  ): Promise<boolean> {
    if (
      this.currentConfig?.dictKeyConfig?.uiControlType !=
        this.uiControlType.Dropdown ||
      !configFilter(this.currentConfig?.dictKeyConfig)
    ) {
      return Promise.resolve(true);
    }
    const promises = [];
    const formGroupOrControl = this.currentConfig?.control;
    const promise = this.refreshDropdownValues(
      this.currentConfig?.dictKeyConfig,
      formGroupOrControl,
      hasModelChanged,
      '',
      formValueWithDefaults,
    );
    promises.push(promise);
    for (let i = 0; i < this.formArray?.length; i++) {
      const promise = this.refreshDropdownValues(
        this.currentConfig?.dictKeyConfig,
        this.formArray.controls[i],
        hasModelChanged,
        '',
        formValueWithDefaults,
      );
      promises.push(promise);
    }
    try {
      await Promise.all(promises);
      return true;
    } catch {
      return false;
    }
  }

  private async validateSelectedValuesForValueControl(
    configFilter: Function,
    hasModelChanged: boolean,
    formValueWithDefaults?: any,
  ): Promise<boolean> {
    if (
      this.currentConfig?.dictValuesConfig?.valueConfig?.uiControlType !=
        this.uiControlType.Dropdown ||
      !configFilter(this.currentConfig?.dictValuesConfig?.valueConfig)
    ) {
      return Promise.resolve(true);
    }
    const promises: Promise<void>[] = [];
    for (let i = 0; i < this.formArray?.length; i++) {
      const promise = this.refreshDropdownValues(
        this.currentConfig?.dictValuesConfig?.valueConfig,
        this.getValueFormControl(this.formArray.controls[i].value),
        hasModelChanged,
        this.formArray.controls[i].value,
        formValueWithDefaults,
      );
      promises.push(promise);
    }
    try {
      await Promise.all(promises);
      return true;
    } catch {
      return false;
    }
  }

  /* Function to validate the model runs */
  validate() {
    this.formArray?.markAllAsTouched();
    this.parentFormGroup
      ?.get(this.currentConfig.property?.toString())
      ?.markAllAsTouched();
    setTimeout(() => {
      this.parentFormGroup
        ?.get(this.currentConfig.property)
        ?.updateValueAndValidity();
    });
    const anyFormArrayInvalid =
      this.formArray?.controls?.some((control) => control.invalid) || false;
    const anyFormGroupInvalid =
      this.parentFormGroup?.get(this.currentConfig.property?.toString())
        ?.invalid || false;
    const erroMsg = anyFormArrayInvalid
      ? 'Key cannot be empty.'
      : 'Please review the field.';
    if (anyFormArrayInvalid || anyFormGroupInvalid) {
      this.currentConfig?.control?.setErrors({ error: erroMsg });
    }
    if (!anyFormArrayInvalid && !anyFormGroupInvalid) {
      this.currentConfig?.control?.setErrors(null);
    }
  }

  public joinMultiKey(arr: (string | string[])[]): string {
    if (this.isCascadeMode()) {
      // Composite key requires at least one value at every level (anything
      // else would produce a key with trailing/empty segments which leaks
      // into the persisted dict and the API payload). An incomplete row
      // returns '' so ``multiSelectDictSubscription`` treats it as "skip"
      // until the user finishes filling the cascade.
      const expected = this.cascadeLevels.length;
      if (expected === 0) return '';
      const segments: string[] = [];
      for (let i = 0; i < expected; i++) {
        const slot = (arr || [])[i];
        let levelVals: string[];
        if (Array.isArray(slot)) {
          levelVals = slot.filter((v) => v != null && v !== '').map(String);
        } else if (slot != null && slot !== '') {
          levelVals = [String(slot)];
        } else {
          levelVals = [];
        }
        if (levelVals.length === 0) return '';
        // Sort within a level so ``[110, 122]`` and ``[122, 110]`` produce
        // the same canonical composite key (avoids dup rows and keeps the
        // persisted dict stable across reorderings of selection).
        levelVals.sort();
        segments.push(levelVals.join(this.MULTI_KEY_SEPARATOR));
      }
      return segments.join(this.COMPOSITE_COL_SEPARATOR);
    }
    return ((arr as string[]) || [])
      .filter((v) => v != null && v !== '')
      .join(this.MULTI_KEY_SEPARATOR);
  }

  private splitMultiKey(key: string): (string | string[])[] {
    if (key == null || key === '') return [];
    if (this.isCascadeMode()) {
      // Pad/truncate to the current number of levels so a saved 2-column
      // config opened against a now-3-column pick (or vice versa) still
      // hydrates predictably. In multi-select-per-level mode, each level
      // segment is further split on ``MULTI_KEY_SEPARATOR`` to recover the
      // original ``string[]``; legacy single-value keys (``"110||AWNING"``)
      // round-trip cleanly because ``split('|')`` returns a single-element
      // array.
      const parts = key.split(this.COMPOSITE_COL_SEPARATOR);
      const n = this.cascadeLevels.length || parts.length;
      const out: (string | string[])[] = [];
      for (let i = 0; i < n; i++) {
        const seg = parts[i] ?? '';
        if (this.isLevelMultiSelect()) {
          out.push(
            seg
              .split(this.MULTI_KEY_SEPARATOR)
              .filter((v) => v != null && v !== ''),
          );
        } else {
          out.push(seg);
        }
      }
      return out;
    }
    return key
      .split(this.MULTI_KEY_SEPARATOR)
      .filter((v) => v != null && v !== '');
  }

  private isMultiSelectKeyDropdown(): boolean {
    return !!(
      this.currentConfig?.dictKeyConfig?.isMultiSelect &&
      this.currentConfig?.dictKeyConfig?.uiControlType ===
        this.uiControlType.Dropdown
    );
  }

  /**
   * Cascade-mode row uniqueness: two breakpoint rows whose per-level value
   * sets intersect at EVERY level produce ambiguous record-to-bucket
   * assignment — any record that matches both rows' filters has no
   * deterministic answer for which splits / bucket configuration applies.
   *
   * This catches both:
   *   1. Exact composite-key duplicates (e.g. both rows = ``["USA"]``),
   *      which previously collapsed into a single dict entry and silently
   *      dropped one of the user's intents.
   *   2. PARTIAL overlaps in multi-select-per-level mode (e.g. Row 1 =
   *      ``["Canada"]`` and Row 2 = ``["Canada", "USA"]``) — the rows have
   *      different composite keys (``Canada`` vs ``Canada|USA``) but
   *      "Canada" records match BOTH rows.
   *
   * After every row-value change, walk the rows in order: the first
   * occurrence of any value-set combination is the "owner"; every later
   * row whose per-level value sets all intersect with a prior row is
   * flagged with ``setErrors({ duplicateCascadeRow: true })``. The error
   * bubbles up through the FormArray to the dict's parent FormGroup, so
   * the surrounding form is marked invalid and downstream submit / Run
   * buttons that gate on form validity get disabled until the user
   * resolves the conflict.
   *
   * Empty / partially-filled rows produce no key (``joinMultiKey`` returns
   * ``''``) and are deliberately ignored — we don't want to flag a row
   * the user is still building.
   *
   * Errors are only added/removed on the ``duplicateCascadeRow`` slot;
   * any other validators on the row control are left untouched.
   */
  public isRowDuplicateInCascade(rowIndex: number): boolean {
    const ctl = this.formArray?.at(rowIndex);
    return !!ctl?.errors?.['duplicateCascadeRow'];
  }

  private updateCascadeDuplicateRowErrors(
    rows: any[],
    combinedKeys: string[],
  ): void {
    if (!this.isCascadeMode()) return;
    // For each prior (already-walked) row, store its per-level value sets
    // so we can test the current row for any-level intersection.
    const priorRowValueSets: Set<string>[][] = [];
    for (let idx = 0; idx < rows.length; idx++) {
      const ctl = this.formArray.at(idx);
      if (!ctl) continue;
      const key = combinedKeys[idx];
      const existing = ctl.errors ? { ...ctl.errors } : {};

      if (!key) {
        // Row not yet complete — never flag it, and don't record it as a
        // prior row (an incomplete row can't conflict with anything yet).
        if (existing['duplicateCascadeRow']) {
          delete existing['duplicateCascadeRow'];
          ctl.setErrors(Object.keys(existing).length ? existing : null, {
            emitEvent: false,
          });
        }
        continue;
      }

      const curValueSets = this.buildCascadeRowValueSets(rows[idx]);
      // Two rows conflict when, for EVERY level, their value sets share
      // at least one element. Equal length is required (handles transient
      // states where cascadeLevels is being rebuilt mid-update).
      const overlaps = priorRowValueSets.some(
        (prior) =>
          prior.length === curValueSets.length &&
          curValueSets.every((set, lvl) => this.setsIntersect(prior[lvl], set)),
      );

      if (overlaps) {
        existing['duplicateCascadeRow'] = true;
        ctl.setErrors(existing, { emitEvent: false });
      } else if (existing['duplicateCascadeRow']) {
        delete existing['duplicateCascadeRow'];
        ctl.setErrors(Object.keys(existing).length ? existing : null, {
          emitEvent: false,
        });
      }

      // Always record the current row's value sets so subsequent rows
      // are checked against this one too — even when the current row
      // was itself flagged as overlapping.
      priorRowValueSets.push(curValueSets);
    }
  }

  /**
   * Build an array of per-level value Sets for a cascade row. In
   * multi-select-per-level mode each slot is already a ``string[]``;
   * in single-select-per-level mode each slot is a single ``string``
   * which we wrap in a 1-element Set. Empty / null slots become empty
   * Sets (which never intersect with anything).
   */
  private buildCascadeRowValueSets(
    row: (string | string[])[] | null | undefined,
  ): Set<string>[] {
    const expected = this.cascadeLevels.length;
    const out: Set<string>[] = [];
    for (let i = 0; i < expected; i++) {
      const slot = (row || [])[i];
      const set = new Set<string>();
      if (Array.isArray(slot)) {
        slot.forEach((v) => {
          if (v != null && v !== '') set.add(String(v));
        });
      } else if (slot != null && slot !== '') {
        set.add(String(slot));
      }
      out.push(set);
    }
    return out;
  }

  private setsIntersect(a: Set<string>, b: Set<string>): boolean {
    if (!a || !b || a.size === 0 || b.size === 0) return false;
    const [small, large] = a.size <= b.size ? [a, b] : [b, a];
    for (const v of small) {
      if (large.has(v)) return true;
    }
    return false;
  }

  private isUniqueAcrossRowsEnabled(): boolean {
    return !!(
      this.isMultiSelectKeyDropdown() &&
      (this.currentConfig?.dictKeyConfig as any)?.enableUniqueRows
    );
  }

  // Build Set of already selected keys excluding a row index (for uniqueness filtering)
  private buildSelectedSet(excludeRowIndex: number = -1): Set<string> {
    const selected = new Set<string>();
    if (!this.isMultiSelectKeyDropdown()) return selected;

    for (let i = 0; i < this.formArray.length; i++) {
      if (i === excludeRowIndex) continue;
      const selArr: string[] = (this.formArray.at(i)?.value || []) as string[];
      (selArr || []).forEach((v) => {
        if (v != null && v !== '') selected.add(v);
      });
    }

    return selected;
  }

  // Compute valid options for a row considering 'uniqueAcrossRows'
  private computeRowOptions(rowIndex: number): string[] {
    const base = [...(this.currentConfig?.dictKeyConfig?.values || [])];
    if (!this.isUniqueAcrossRowsEnabled()) {
      return base;
    }
    const taken = this.buildSelectedSet(rowIndex);
    return base.filter((v) => !taken.has(v));
  }

  // Refresh all per-row options cache and prune invalid selections
  private refreshAllKeyOptionsForMultiSelect(): void {
    if (!this.isMultiSelectKeyDropdown()) return;

    this.keyOptionsPerRow = [];
    for (let i = 0; i < this.formArray.length; i++) {
      const options = this.computeRowOptions(i);
      this.keyOptionsPerRow[i] = options;

      if (this.isInsertingBlankRow || this.isDeletingRow) continue;
      if (this.isUniqueAcrossRowsEnabled()) {
        const current = (this.formArray.at(i).value || []) as string[];
        const filtered = (current || []).filter((v) => options.includes(v));
        if (filtered.length !== (current || []).length) {
          this.formArray.at(i).setValue(filtered, { emitEvent: false });
        }
      }
    }
  }

  public getKeyOptionsForRow(rowIndex: number): string[] {
    if (!this.isMultiSelectKeyDropdown())
      return this.currentConfig?.dictKeyConfig?.values || [];
    if (
      !this.keyOptionsPerRow[rowIndex] ||
      !this.keyOptionsPerRow[rowIndex].length
    ) {
      this.keyOptionsPerRow[rowIndex] = this.computeRowOptions(rowIndex);
    }
    return this.keyOptionsPerRow[rowIndex];
  }

  /**
   * Returns the single-select key dropdown's values with any value already
   * picked by another row removed. The row's own current value is preserved
   * so the displayed selection stays visible.
   *
   * Standalone of the multi-select uniqueness path (``getKeyOptionsForRow`` /
   * ``computeRowOptions``); used only when ``dictKeyConfig.enableUniqueRows``
   * is set on a non-multi-select dropdown.
   */
  public getDistinctKeyOptionsForSingleSelectRow(rowIndex: number): any[] {
    const base = (this.currentConfig?.dictKeyConfig?.values || []) as any[];
    const otherVals: string[] = [];
    const len = this.formArray?.length || 0;
    for (let i = 0; i < len; i++) {
      if (i === rowIndex) continue;
      const v = this.formArray.at(i)?.value;
      otherVals.push(v == null ? '' : String(v));
    }
    const signature = otherVals.join('|');
    const cached = this._keyOptionsCache.get(rowIndex);
    if (cached && cached.base === base && cached.signature === signature) {
      return cached.result;
    }
    const taken = new Set<string>(otherVals.filter((v) => v !== ''));
    const result = base.filter((v) => {
      const key = this.getKeyOptionStoredValue(v);
      return !taken.has(String(key));
    });
    this._keyOptionsCache.set(rowIndex, { signature, base, result });
    return result;
  }

  /**
   * Stored value derived from an option object, mirroring the standalone
   * dropdown control: ``dropdownID`` wins; otherwise when
   * ``optionDisplayFields`` is provided the value is the first field's value
   * (joined with the second field in parentheses for the 2+ case so the
   * stored key matches the user-visible label, e.g. ``"Astro MILL (May 04,
   * 2026)"``); else the option itself.
   */
  public getKeyOptionStoredValue(option: any): any {
    const cfg = this.currentConfig?.dictKeyConfig;
    if (option == null) return option;
    if (cfg?.dropdownID) {
      return option?.[cfg.dropdownID]?.toString();
    }
    if (
      Array.isArray(cfg?.optionDisplayFields) &&
      cfg.optionDisplayFields.length > 0
    ) {
      const fields = cfg.optionDisplayFields;
      const first = option?.[fields[0]];
      if (fields.length > 1 && option?.[fields[1]] != null) {
        return `${first} (${option[fields[1]]})`;
      }
      return first;
    }
    return option?.toString();
  }

  /**
   * Display text for an option in the single-select key dropdown. Honors
   * ``dropdownLabel`` first; falls back to the stored value (which already
   * incorporates ``optionDisplayFields`` when configured).
   */
  public getKeyOptionLabelText(option: any): string {
    const cfg = this.currentConfig?.dictKeyConfig;
    if (option == null) return '';
    if (cfg?.dropdownLabel && option?.[cfg.dropdownLabel] != null) {
      return option[cfg.dropdownLabel]?.toString();
    }
    if (
      Array.isArray(cfg?.optionDisplayFields) &&
      cfg.optionDisplayFields.length > 0
    ) {
      const v = this.getKeyOptionStoredValue(option);
      return v == null ? '' : String(v);
    }
    return option?.toString() ?? '';
  }

  /**
   * Returns the single-select key dropdown's options for ``rowIndex``,
   * preserving the ``isGrouped`` shape of ``dictKeyConfig.values``: flat
   * ``[opt, ...]`` when not grouped, or ``[{label, options: [...]}, ...]``
   * when grouped. ``enableUniqueRows`` filtering is applied to the option
   * list (within each group when grouped) so values already chosen by other
   * rows do not appear in this row's picker.
   */
  public getSingleSelectKeyOptionsToShow(rowIndex: number): any[] {
    const cfg = this.currentConfig?.dictKeyConfig;
    const base = (cfg?.values || []) as any[];
    if (!cfg?.isGrouped) {
      return cfg?.enableUniqueRows
        ? this.getDistinctKeyOptionsForSingleSelectRow(rowIndex)
        : base;
    }
    if (!cfg?.enableUniqueRows) {
      return base;
    }
    const otherVals: string[] = [];
    const len = this.formArray?.length || 0;
    for (let i = 0; i < len; i++) {
      if (i === rowIndex) continue;
      const v = this.formArray.at(i)?.value;
      otherVals.push(v == null ? '' : String(v));
    }
    const signature = otherVals.join('|');
    const cached = this._keyOptionsCache.get(rowIndex);
    if (cached && cached.base === base && cached.signature === signature) {
      return cached.result;
    }
    const taken = new Set<string>(otherVals.filter((v) => v !== ''));
    const result = base
      .map((group) => ({
        label: group?.label,
        options: (group?.options || []).filter((option: any) => {
          const stored = this.getKeyOptionStoredValue(option);
          return !taken.has(String(stored));
        }),
      }))
      .filter((group) => (group.options || []).length > 0);
    this._keyOptionsCache.set(rowIndex, { signature, base, result });
    return result;
  }

  public getCombinedKeyForRow(i: number): string {
    if (
      !this.currentConfig?.dictKeyConfig?.isMultiSelect &&
      !this.isCascadeMode()
    ) {
      return this.formArray.at(i)?.value;
    }
    const selArr = (this.formArray.at(i)?.value || []) as (string | string[])[];
    return this.joinMultiKey(selArr);
  }

  // Select All checkbox related helpers
  public isRowAllSelected(rowIndex: number): boolean {
    if (!this.isMultiSelectKeyDropdown()) return false;
    const options = this.isUniqueAcrossRowsEnabled()
      ? this.getKeyOptionsForRow(rowIndex)
      : this.currentConfig?.dictKeyConfig?.values || [];

    const value: string[] = (this.formArray.at(rowIndex)?.value ||
      []) as string[];
    const selectedVisibleCount = (value || []).filter((v) =>
      options.includes(v),
    ).length;
    return options.length > 0 && selectedVisibleCount === options.length;
  }

  public isRowIndeterminate(rowIndex: number): boolean {
    if (!this.isMultiSelectKeyDropdown()) return false;
    const options = this.isUniqueAcrossRowsEnabled()
      ? this.getKeyOptionsForRow(rowIndex)
      : this.currentConfig?.dictKeyConfig?.values || [];
    const value: string[] = (this.formArray.at(rowIndex)?.value ||
      []) as string[];
    const selectedVisibleCount = (value || []).filter((v) =>
      options.includes(v),
    ).length;
    return selectedVisibleCount > 0 && selectedVisibleCount < options.length;
  }

  public selectAllToggle(
    rowIndex: number,
    changeEvt: { checked: boolean },
    visibleOptions: string[],
  ): void {
    if (!this.isMultiSelectKeyDropdown()) return;
    const control = this.formArray.at(rowIndex) as FormControl;
    const current = (control.value || []) as string[];
    if (changeEvt?.checked) {
      const preserved = current.filter((v) => !visibleOptions.includes(v));
      control.setValue([...preserved, ...visibleOptions], { emitEvent: true });
    } else {
      const remaining = current.filter((v) => !visibleOptions.includes(v));
      control.setValue(remaining, { emitEvent: true });
    }
  }

  // Cascade-level Select/Deselect All helpers (Breakpoints under
  // categorical_vars). Operate on the per-level slot inside the row's
  // ``(string|string[])[]`` value rather than the flat row value used by
  // ``isRowAllSelected`` / ``selectAllToggle``.
  private getCascadeLevelOptionValues(levelIndex: number): string[] {
    const level = this.cascadeLevels[levelIndex];
    const values = (level?.values || []) as any[];
    return values.map((v) =>
      level?.dropdownID ? v?.[level.dropdownID]?.toString() : v?.toString(),
    );
  }

  public isCascadeLevelAllSelected(
    rowIndex: number,
    levelIndex: number,
  ): boolean {
    if (!this.isLevelMultiSelect()) return false;
    const options = this.getCascadeLevelOptionValues(levelIndex);
    if (options.length === 0) return false;
    const value = this.getCascadeLevelValueArray(rowIndex, levelIndex);
    const selectedVisible = value.filter((v) => options.includes(v)).length;
    return selectedVisible === options.length;
  }

  public isCascadeLevelIndeterminate(
    rowIndex: number,
    levelIndex: number,
  ): boolean {
    if (!this.isLevelMultiSelect()) return false;
    const options = this.getCascadeLevelOptionValues(levelIndex);
    const value = this.getCascadeLevelValueArray(rowIndex, levelIndex);
    const selectedVisible = value.filter((v) => options.includes(v)).length;
    return selectedVisible > 0 && selectedVisible < options.length;
  }

  public cascadeSelectAllToggle(
    rowIndex: number,
    levelIndex: number,
    changeEvt: { checked: boolean },
  ): void {
    if (!this.isLevelMultiSelect()) return;
    const options = this.getCascadeLevelOptionValues(levelIndex);
    const current = this.getCascadeLevelValueArray(rowIndex, levelIndex);
    const newValue = changeEvt?.checked
      ? [...current.filter((v) => !options.includes(v)), ...options]
      : current.filter((v) => !options.includes(v));
    this.setCascadeLevelValue(rowIndex, levelIndex, newValue);
    // Selecting/clearing this level invalidates downstream level selections,
    // matching the behavior of a normal selection change at this level.
    this.onCascadeLevelChanged(rowIndex, levelIndex);
  }

  ngOnDestroy() {
    this.jsonDynamicService.removeDynamicDictControl(this.id);
    this.currentConfig?.control?.setErrors(null);
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    this.resetCascadeLevelControlsCache();
  }
}
